import type { UIRouterReact } from "@uirouter/react/lib/core";

import { isOptedInToDigitalSuite } from "app/common/services/solutions2Helper";
import { swSettings } from "app/common/services/swSettings";
import { isAeoSolution } from "app/common/utils/isAeoSolution";
import { DEFAULT_HOMEPAGE_PREFERENCE_KEY } from "app/components/modals/constants";
import { SESSION_STORAGE_KEY_PACKAGE } from "app/pages/activation/components/AddonPopup/constants";
import { routeToActivation } from "app/pages/activation/routing/redirectHooks/routeToActivation";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { stateService } from "app/user-data/StateService";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { FORCE_PRO_PARAM } from "single-spa/react-app/router/NavigationUtils";
import { redirectLiteToPro } from "single-spa/react-app/router/redirectFromRegistrationUtils";
import { EParamsName } from "single-spa/react-app/router/types";
import { cleanNewLoaderParams } from "single-spa/react-app/router/utils/cleanNewLoaderParams";

import { newUserRouting } from "./utils/paramsRouting/newUserRouting";
import { isAppSolution, isTrialPlan, redirectToAppsSolution } from "app/common/utils/isAppSolution";
import { allTrackers } from "app/services/track/track";

export const SALES_HOMEPAGE = "sales";
const PRO_HOMEPAGE = "/digitalsuite/home";
const AI_BRAND_VISIBILITY_HOMEPAGE = "/digitalsuite/ai-brand-visibility/home";

export const routerOtherwiseRules = (router: UIRouterReact) => {
    router.urlService.rules.otherwise((matchValue, urlParts) => {
        const defaultHomepage = PreferencesService.get(DEFAULT_HOMEPAGE_PREFERENCE_KEY) as string;

        const urlParams = new URLSearchParams(window.location.search);
        const { hasSI, hasMR, hasDM } = swSettings.user;

        const packagesCount = [hasSI, hasMR, hasDM].filter(Boolean).length;
        const forcePro = urlParams.has(FORCE_PRO_PARAM);
        const isSI = packagesCount === 1 ? hasSI : hasSI && defaultHomepage === SALES_HOMEPAGE;

        //clean-up new first loader query params
        if (urlParams.has(EParamsName.SHOULD_SHOW_LOADER)) {
            cleanNewLoaderParams(urlParams);
        }

        //pro
        if (forcePro) {
            //remove the parameter from the URL
            urlParams.delete(FORCE_PRO_PARAM);
            const newUrl = new URL(window.location.href);
            newUrl.search = urlParams.toString();
            window.history.replaceState({}, "", newUrl);

            return routeToActivation() || PRO_HOMEPAGE;
        }

        //account
        // @ts-ignore FIXME: strict migration - 'urlParts' is possibly 'undefined' — uirouter's otherwise callback always provides urlParts at runtime
        if (urlParts.path.startsWith("/account/")) {
            // @ts-ignore FIXME: strict migration - 'urlParts' is possibly 'undefined' — uirouter's otherwise callback always provides urlParts at runtime
            window.location.href = swSettings.swsites.login + urlParts.path.slice(8);
            return;
        }

        //external
        // @ts-ignore FIXME: strict migration - 'urlParts' is possibly 'undefined' — uirouter's otherwise callback always provides urlParts at runtime
        const [, externalRedirect] = /^\/redirect=(.+)/i.exec(urlParts.path) || ["", ""];
        if (externalRedirect) {
            const { stateName, params, serializedState } = JSON.parse(decodeURIComponent(atob(externalRedirect)));
            if (stateName) {
                return proNavigator.go(stateName, params, { location: "replace" });
            }
            if (serializedState && !stateName) {
                window.location.href = stateService.deSerializeState(serializedState);
                return;
            }
        }

        //viewer mode
        const hasLicense = swSettings.components.Home.resources.HasLicense;
        if (!hasLicense) {
            allTrackers.dispatchTrackEvent("Internal Link", "open", "Dashboard Header/Viewers Redirect");
            return "/dashboard";
        }

        //lite
        if (urlParams.has(EParamsName.ACTION)) {
            const actionParam = urlParams.get(EParamsName.ACTION);
            if (actionParam === EParamsName.LITE_REDIRECT) {
                const mainPlan = swSettings.user.plan;
                const shopperIntelligencePlanName = "Shopper Intelligence";
                if (mainPlan.includes(shopperIntelligencePlanName)) {
                    window.location.href = swSettings.swsites.shopper;
                    return;
                }
            }
            const redirectState = redirectLiteToPro(isSI);
            if (redirectState) {
                return proNavigator.go(redirectState.state, redirectState.params);
            }
        }

        if (urlParams.has(EParamsName.NEW_USER)) {
            const result = newUserRouting(urlParams);

            if (result !== undefined) {
                return result;
            }
        }

        if (urlParams?.has(EParamsName.PACKAGE)) {
            sessionStorage.setItem(
                SESSION_STORAGE_KEY_PACKAGE,
                JSON.stringify({ purchasedPackage: urlParams.get(EParamsName.PACKAGE) }),
            );

            //remove the parameter from the URL
            urlParams.delete(EParamsName.PACKAGE);
            const newUrl = new URL(window.location.href);
            newUrl.search = urlParams.toString();
            window.history.replaceState({}, "", newUrl);
        }

        //ai solutions
        if (isAeoSolution(swSettings)) {
            return AI_BRAND_VISIBILITY_HOMEPAGE;
        }

        // app solutions
        if (isAppSolution(swSettings) && isTrialPlan(swSettings)) {
            redirectToAppsSolution(swSettings);
            return;
        }

        //sales
        if (isSI) {
            return `/${SALES_HOMEPAGE}`;
        }

        //pro
        if (packagesCount >= 2) {
            return routeToActivation() || PRO_HOMEPAGE;
        } else {
            //digital marketing
            if (isOptedInToDigitalSuite() && defaultHomepage !== SALES_HOMEPAGE) {
                return routeToActivation() || PRO_HOMEPAGE;
            }
        }

        //fallback
        return swSettings.components.Home.resources.defaulturl.substr(2);
    });
};
